import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import { isAuthenticated } from "../services/auth.js";

function formatApiError(err) {
  const data = err.response?.data;
  if (typeof data === "string") return data;
  if (data?.detail) return String(data.detail);
  if (Array.isArray(data?.non_field_errors)) {
    return data.non_field_errors.join(" ");
  }
  if (data && typeof data === "object") {
    const parts = [];
    for (const [key, val] of Object.entries(data)) {
      if (Array.isArray(val)) parts.push(`${key}: ${val.join(" ")}`);
      else if (val != null) parts.push(`${key}: ${String(val)}`);
    }
    if (parts.length) return parts.join(" · ");
  }
  return err.message || "Something went wrong.";
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get("next") || "/";

  const { login, ready, isAuthenticated: authed } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!ready) return;
    if (authed || isAuthenticated()) {
      navigate(nextPath, { replace: true });
    }
  }, [ready, authed, navigate, nextPath]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (!email || !password) {
        setError("Enter email and password.");
        setLoading(false);
        return;
      }
      await login({ email, password });
      navigate(nextPath, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const formDisabled = loading || !ready;

  return (
    <article className="site-page">
      <div className="site-page__inner site-page__inner--narrow">
        <p className="site-page__eyebrow">Account</p>
        <h1 className="site-page__title">Sign in</h1>
        <p className="site-page__lead">
          Log into AICSNetworks.
        </p>

        {error ? <p className="auth-form__error">{error}</p> : null}

        <form className="auth-form" onSubmit={onSubmit}>
          <label className="auth-form__field">
            <span>Email</span>
            <input
              name="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              disabled={formDisabled}
              required
            />
          </label>

          <label className="auth-form__field">
            <span>Password</span>
            <input
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(ev) => setPassword(ev.target.value)}
              disabled={formDisabled}
              required
            />
          </label>

          <button
            className="auth-form__submit"
            type="submit"
            disabled={formDisabled}
          >
            {loading ? "Working…" : "Sign in"}
          </button>
        </form>

        <p className="site-page__muted">
          <Link to="/">← Back to home</Link>
        </p>
      </div>
    </article>
  );
}
